import { useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface Props { open: boolean; imageUrl: string; onClose: () => void; onDone: (blob: Blob, purpose: "ai" | "replace") => Promise<void>; simple?: { title: string; description: string; confirmLabel: string } }

async function cropToBlob(url: string, area: Area, rotation: number): Promise<Blob> {
  const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = rej; i.src = url; });
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad)), cos = Math.abs(Math.cos(rad));
  const bw = img.width * cos + img.height * sin, bh = img.width * sin + img.height * cos;
  const rc = document.createElement("canvas"); rc.width = bw; rc.height = bh;
  const rctx = rc.getContext("2d")!; rctx.translate(bw / 2, bh / 2); rctx.rotate(rad); rctx.drawImage(img, -img.width / 2, -img.height / 2);
  const out = document.createElement("canvas"); out.width = area.width; out.height = area.height;
  out.getContext("2d")!.drawImage(rc, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return new Promise((res) => out.toBlob((b) => res(b!), "image/jpeg", 0.92));
}

export function CropDialog({ open, imageUrl, onClose, onDone, simple }: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState<number>(4 / 3);
  const [area, setArea] = useState<Area | null>(null);
  const [busy, setBusy] = useState<"" | "ai" | "replace">("");

  const finish = async (purpose: "ai" | "replace") => {
    if (!area) return;
    setBusy(purpose);
    try { await onDone(await cropToBlob(imageUrl, area, rotation), purpose); onClose(); } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="crop-dialog">
        <DialogHeader><DialogTitle>{simple?.title || "Crop & reposition before photo"}</DialogTitle><DialogDescription>{simple?.description || "Straighten and frame the house so the AI mockup focuses on the right area. The original photo is always kept."}</DialogDescription></DialogHeader>
        <div className="relative h-[420px] overflow-hidden rounded-md bg-gray-900" data-testid="crop-area">
          <Cropper image={imageUrl} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation} onCropComplete={(_, px) => setArea(px)} />
        </div>
        <div className="grid grid-cols-3 gap-4 text-xs text-gray-600">
          <label>Zoom<input data-testid="crop-zoom" type="range" min={1} max={4} step={0.02} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="mt-1 w-full accent-blue-700" /></label>
          <label>Straighten ({rotation}°)<input data-testid="crop-rotation" type="range" min={-20} max={20} step={0.5} value={rotation} onChange={(e) => setRotation(Number(e.target.value))} className="mt-1 w-full accent-blue-700" /></label>
          <label>Frame<select data-testid="crop-aspect" className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1" value={aspect} onChange={(e) => setAspect(Number(e.target.value))}><option value={4 / 3}>4:3 (landscape)</option><option value={16 / 10}>16:10 (wide)</option><option value={1}>1:1 (square)</option><option value={3 / 2}>3:2</option></select></label>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="crop-cancel-btn">Cancel</Button>
          {simple ? (
            <Button disabled={!!busy} onClick={() => finish("replace")} className="bg-blue-700 hover:bg-blue-800" data-testid="crop-confirm-btn">{busy ? "Saving…" : simple.confirmLabel}</Button>
          ) : <>
            <Button variant="outline" disabled={!!busy} onClick={() => finish("ai")} data-testid="crop-use-for-ai-btn">{busy === "ai" ? "Saving…" : "Use crop for AI only"}</Button>
            <Button disabled={!!busy} onClick={() => finish("replace")} className="bg-blue-700 hover:bg-blue-800" data-testid="crop-replace-btn">{busy === "replace" ? "Saving…" : "Replace before photo"}</Button>
          </>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
